import { Link } from 'react-router-dom';
import { Bot, Zap, Shield, BarChart, Clock, Smartphone, CheckCircle, TrendingUp, Users, MessageCircle } from 'lucide-react';
// @ts-ignore: module has no declaration file
import useDynamicSEO from '../hooks/dynamicSEO';

const ParkingLotAutomationSoftware = () => {
    useDynamicSEO({
      title: "Parking Lot Automation Software | Entry, Exit & Revenue App",
      description: "Eliminate manual handling with automated Parking Lot Automation Software. Improve flow, prevent revenue loss & simplify operations with smart digital control.",
          keywords:["Parking Management System","Event & Exhibition Parking Management","Parking Lot Management App","Digital Parking Solutions"," Vehicle entry exit management system","AI Parking Management Platform","Parking Lot Automation Software", "Parking revenue management app", "Tourist Attraction Parking Software", "Cinema & Multiplex Parking System"],
      ogTitle: "Parking Lot Automation Software | Entry, Exit & Revenue App",
      ogDescription: "Eliminate manual handling with automated Parking Lot Automation Software. Improve flow, prevent revenue loss & simplify operations with smart digital control."
    });


  const handleWhatsAppClick = () => {
    const phoneNumber = '9384376329';
    const message = encodeURIComponent('Hi, I would like to request a demo of Park Hive Parking Lot Automation Software for fully automated parking operations.');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const automationFeatures = [
    {
      icon: <Bot className="w-8 h-8 text-blue-600" />,
      title: "Automated Entry & Exit",
      description: "Instant digital scanning eliminates manual tickets and handwritten processes completely"
    },
    {
      icon: <BarChart className="w-8 h-8 text-green-600" />,
      title: "Real-time Monitoring",
      description: "Live occupancy tracking and flow monitoring from anywhere, anytime"
    },
    {
      icon: <Zap className="w-8 h-8 text-yellow-600" />,
      title: "Automatic Pricing",
      description: "Intelligent calculation based on duration and preset rules - no calculators needed"
    },
    {
      icon: <Shield className="w-8 h-8 text-purple-600" />,
      title: "Digital Payments",
      description: "Secure, transparent payment processing with zero cash handling risks"
    },
    {
      icon: <Users className="w-8 h-8 text-red-600" />,
      title: "Multi-operator Access",
      description: "Team management with complete accountability and activity logging"
    },
    {
      icon: <Clock className="w-8 h-8 text-indigo-600" />,
      title: "24/7 Operations",
      description: "Continuous automation that works during peak hours and off-peak times"
    }
  ];

  const businessImpacts = [
    {
      title: "Multiplex Theaters",
      impact: "70% faster entry processing during weekend rush hours"
    },
    {
      title: "Hospital Complexes",
      impact: "85% reduction in payment disputes and queue formation"
    },
    {
      title: "Airport Parking",
      impact: "60% improvement in vehicle flow during peak travel times"
    },
    {
      title: "University Campuses",
      impact: "90% elimination of manual ticketing errors and revenue gaps"
    }
  ];

  const automationBenefits = [
    "Accuracy without oversight",
    "Revenue without risk", 
    "Speed without stress",
    "Transparency without arguments",
    "Scalability without manpower expansion"
  ];

  const faqData = [
    {
      question: "Does automation require heavy hardware installation?",
      answer: "No — Park Hive runs through the mobile app. Hardware is optional."
    },
    {
      question: "Can automation reduce staffing needs?",
      answer: "Yes — it minimizes manual workload drastically."
    },
    {
      question: "Is it suitable for temporary or seasonal parking spaces?",
      answer: "Absolutely — setup takes only minutes."
    },
    {
      question: "Can I manage multiple locations?",
      answer: "Yes — automation works across all sites from one dashboard."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <Bot className="w-16 h-16 text-blue-300" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Parking Lot Automation Software
          </h1>
          <p className="text-2xl md:text-3xl mb-8 text-blue-200">
            Fully Automated Parking Operations Without Manual Dependency
          </p>
          <p className="text-xl mb-10 max-w-3xl mx-auto">
            Faster Flow. Zero Revenue Leakage. Complete Operational Confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleWhatsAppClick}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Request Live Demo
            </button>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 text-center"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Parking Lots Must Be Automated
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              Parking lots are the first and last touchpoint of every visitor experience. Manual operations create chaos, frustration, and revenue loss.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">The Manual Parking Problem</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Manual operations lead to slow throughput</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Human errors ruin revenue control</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Cash handling creates leakage and risk</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">Staff dependency limits scalability</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">No real-time visibility means no control</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-green-600 mb-4">Automation Solves Everything</h3>
              <p className="text-gray-700 mb-6">
                When software controls the flow, accuracy is guaranteed, money is tracked, decisions are instant, and efficiency becomes natural.
              </p>
              <div className="bg-green-50 p-6 rounded-lg">
                <p className="text-lg font-semibold text-green-800 text-center">
                  "Automation is not a luxury anymore. It is survival."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Automation Works */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              How Park Hive Automates Parking End to End
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              The moment a vehicle arrives, intelligent automation takes over - from entry scanning to exit billing, everything happens instantly.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">1. Instant Entry</h3>
              <p className="text-gray-700">
                Phone camera scans number plate automatically. Vehicle type identified and timing begins without handwritten slips.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">2. Automated Tracking</h3>
              <p className="text-gray-700">
                System tracks duration automatically. Preset hourly or slab pricing applies without calculations or confusion.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">3. Digital Exit</h3>
              <p className="text-gray-700">
                Exit scan calculates exact billing. Customers pay digitally and move freely. No calculators, no guesswork.
              </p>
            </div>
          </div>

          <div className="mt-16 bg-blue-50 p-8 rounded-xl">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
              Real-time Owner Control
            </h3>
            <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto">
              Owners and managers watch everything live — occupancy, transactions, revenue, operator activity, and flow — from anywhere. 
              Automation turns time into efficiency, data into control, and unpredictability into confidence.
            </p>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Core Automation Features Built for Real Operations
            </h2>
            <p className="text-xl text-gray-700">
              Complete automation power to scale and perform smoothly under any pressure
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {automationFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-700">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Advanced Capabilities</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Configurable pricing (hourly, daily, weekend, monthly)</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Multi-location management from one account</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Offline-capable during connectivity drops</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Exportable records for accounting and audit</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Built for High-Pressure Environments</h3>
              <p className="text-gray-700 mb-4">
                Whether it's weekend multiplex rush, sudden hospital traffic, airport loading, university movement, or event overload — 
                automation keeps vehicles moving smoothly at peak stress levels.
              </p>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="font-semibold text-green-800 text-center">
                  Parking no longer collapses when crowds arrive. It accelerates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Impact */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Proven Business Impact Across Industries
            </h2>
            <p className="text-xl text-gray-700">
              Real results from automated parking operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {businessImpacts.map((impact, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl text-center">
                <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 mb-3">{impact.title}</h3>
                <p className="text-blue-600 font-semibold">{impact.impact}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-8 rounded-xl">
            <h3 className="text-2xl font-bold text-center mb-6">Why Parking Owners Choose Automation</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {automationBenefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                  <p className="font-semibold">{benefit}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-xl font-bold mt-8">
              Automation is freedom from firefighting.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Experience the Future of Parking Today
          </h2>
          <p className="text-xl mb-8">
            See what a fully automated parking system feels like in real time. Watch movement, monitoring, billing, and reporting happen without manual effort.
          </p>
          <button 
            onClick={handleWhatsAppClick}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 flex items-center justify-center gap-2 mx-auto"
          >
            <MessageCircle className="w-5 h-5" />
            Request a Demo - Transform Your Parking Space
          </button>
          <p className="text-gray-300 mt-4">
            Transform your parking space with intelligent automation.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-700">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Message */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Parking lots were never meant to run on chaos.
          </h2>
          <p className="text-xl mb-6">
            Automation is not an upgrade — it is a revolution.
          </p>
          <p className="text-lg mb-8">
            The next generation of parking is fast, transparent, intelligent, and unmanned. 
            The question is not whether automation is needed. It is how soon you choose to adopt it.
          </p>
          <div className="bg-blue-800 p-6 rounded-xl">
            <p className="text-2xl font-bold">
              Park Hive makes that transformation effortless.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ParkingLotAutomationSoftware;